//eslint-disable-next-line
//@ts-nocheck

import {
  CustomIndicator,
  PineJS,
  RawStudyMetaInfoId,
  StudyInputType,
  StudyPlotType,
  StudyTargetPriceScale,
} from "@/lib/datafeed/tvTypes.ts";

import { LineStudyPlotStyle } from "@/lib/datafeed/utils.ts";

import {
  AccessCheck,
  ParseSymbol,
} from "@/lib/indicators/indicators-common-functions";

import { IIndicatorOptions } from "@/domain/interfaces/general.interface";

enum PlotSymbolSize {
  Auto = "auto",
  Tiny = "tiny",
  Small = "small",
  Normal = "normal",
  Large = "large",
  Huge = "huge",
}

enum MarkLocation {
  AboveBar = "AboveBar",
  BelowBar = "BelowBar",
  Top = "Top",
  Bottom = "Bottom",
  Right = "Right",
  Left = "Left",
  Absolute = "Absolute",
  AbsoluteUp = "AbsoluteUp",
  AbsoluteDown = "AbsoluteDown",
}

export const strategies = (
  PineJS: PineJS,
  options?: IIndicatorOptions,
): CustomIndicator => {
  return {
    name: "Strategies",
    metainfo: {
      _metainfoVersion: 51,
      id: "strategies@tv-basicstudies-1" as RawStudyMetaInfoId,
      description: "[SFM] Strategies",
      shortDescription: "[SM] Strategies",
      is_hidden_study: false,
      is_price_study: true,
      format: {
        type: "price",
        precision: 2,
      },
      plots: [
        {
          id: "sellShapePlot",
          type: StudyPlotType.Shapes,
        },
        {
          id: "sellPricePlot",
          type: StudyPlotType.Shapes,
        },
        {
          id: "buyShapePlot",
          type: StudyPlotType.Shapes,
        },
        {
          id: "buyPricePlot",
          type: StudyPlotType.Shapes,
        },
        {
          id: "backgroundPlot",
          type: StudyPlotType.BgColorer,
          palette: "backgroundPalette",
        },
      ],
      palettes: {
        backgroundPalette: {
          colors: [{ name: "Sell signal" }, { name: "Buy signal" }],
          valToIndex: {
            0: 0,
            1: 1,
          },
        },
      },
      filledAreas: [],
      defaults: {
        palettes: {
          backgroundPalette: {
            colors: [
              { color: "rgba(178, 24, 44, 0.25)" },
              { color: "rgba(60, 166, 75, 0.25)" },
            ],
          },
        },
        styles: {
          sellShapePlot: {
            location: MarkLocation.AboveBar,
            visible: true,
            plottype: "shape_label_down",
            textColor: "rgba(178, 24, 44, 0.75)",
            color: "rgba(178, 24, 44, 0.75)",
          },
          sellPricePlot: {
            location: MarkLocation.Absolute,
            visible: true,
            plottype: "shape_circle",
            textColor: "rgba(178, 24, 44, 0.75)",
            color: "rgba(178, 24, 44, 0.75)",
          },
          buyShapePlot: {
            location: MarkLocation.BelowBar,
            visible: true,
            plottype: "shape_label_up",
            textColor: "rgba(60, 166, 75, 0.75)",
            color: "rgba(60, 166, 75, 0.75)",
          },
          buyPricePlot: {
            location: MarkLocation.Absolute,
            visible: true,
            plottype: "shape_circle",
            textColor: "rgba(60, 166, 75, 0.75)",
            color: "rgba(60, 166, 75, 0.75)",
          },
        },
        precision: 2,
        inputs: {
          symbolInput: "",
          strategyInput: "CM1",
        },
      },
      styles: {
        sellShapePlot: {
          isHidden: false,
          size: PlotSymbolSize.Normal,
          title: "Sell signal",
        },
        sellPricePlot: {
          isHidden: false,
          size: PlotSymbolSize.Tiny,
          title: "Sell signal",
        },
        buyShapePlot: {
          isHidden: false,
          size: PlotSymbolSize.Normal,
          title: "Buy signal",
        },
        buyPricePlot: {
          isHidden: false,
          size: PlotSymbolSize.Tiny,
          title: "Buy signal",
        },
        backgroundPlot: {
          title: "Background",
          histogramBase: 0,
        },
      },
      inputs: [
        {
          id: "symbolInput",
          name: "Symbol",
          defval: "",
          type: StudyInputType.Symbol,
        },
        {
          id: "strategyInput",
          name: "Strategy",
          defval: "CM1",
          options: ["CM1", "CM2", "CM3"],
          type: StudyInputType.Text,
        },
      ],
    },

    constructor: function (this: LibraryPineStudy<IPineStudyResult>) {
      this.init = function (context, inputCallback) {
        this._context = context;
        this._input = inputCallback;

        const period: number = PineJS.Std.period(this._context);

        const symbol: string = this._input(0) || this._context.symbol.info.name;
        const baseSymbol: string = ParseSymbol(symbol);

        const strategyInput: string = this._input(1);

        const strategySymbolMap: Record<string, string> = {
          CM1: "STRATEGY_CM1",
          CM2: "STRATEGY_CM2",
          CM3: "STRATEGY_CM3",
        };

        const strategySuffix: string =
          strategySymbolMap[strategyInput] || `STRATEGY_${strategyInput}`;
        const strategySymbol: string = `${baseSymbol}#${strategySuffix}`;
        this._context.new_sym(strategySymbol, period);
      };

      this.main = function (context, inputCallback) {
        this._context = context;
        this._input = inputCallback;

        // Market data parsing
        this._context.select_sym(1);
        const strategyTime: any = this._context.new_var(
          this._context.symbol.time,
        );
        const directionArray: any = this._context.new_var(
          PineJS.Std.open(this._context),
        );
        const priceArray: any = this._context.new_var(
          PineJS.Std.high(this._context),
        );
        this._context.select_sym(0);
        const mainSymbolTime: any = this._context.new_var(
          this._context.symbol.time,
        );
        const direction: number = directionArray.adopt(
          strategyTime,
          mainSymbolTime,
          0,
        );
        const price: number = priceArray.adopt(strategyTime, mainSymbolTime, 0);

        // Calculations
        let [sellSignal, buySignal, bgcolor, sellSignalPrice, buySignalPrice]: [
          number,
          number,
          number,
          number,
          number,
        ] = [NaN, NaN, NaN, NaN, NaN];
        if (direction === 0) {
          sellSignal = 0;
          bgcolor = 0;
          sellSignalPrice = price;
        } else if (direction === 1) {
          buySignal = 1;
          bgcolor = 1;
          buySignalPrice = price;
        }

        const shouldShowData: boolean = AccessCheck(
          PineJS,
          this._context,
          options,
        );

        if (shouldShowData) {
          return [
            sellSignal,
            sellSignalPrice,
            buySignal,
            buySignalPrice,
            bgcolor,
          ];
        } else {
          return [NaN, NaN, NaN, NaN, NaN];
        }
      };
    },
  };
};
